import MetricCard from "../ui/MetricCard";
import { formatUSD, formatPercent, formatMonths } from "../../lib/formatters";
import {
  calculateMRR, calculateARR, calculateCashCollected, calculateOutstandingAR,
  calculateExpectedRevenue, calculateMonthlyChurn, calculateAnnualChurn,
  calculateNewClientsThisMonth, calculateChurnedThisMonth, calculateProjectRevenueYTD,
  calculateAverageLTV, calculateAverageLifetime,
} from "../../lib/calculations";
import { startOfMonth, endOfMonth, today, addDays } from "../../lib/dateUtils";

export default function KPICards({ clients, offers, payments }) {
  const t = today();
  const monthStart = startOfMonth(t);
  const monthEnd = endOfMonth(t);
  const yearStart = new Date(t.getFullYear(), 0, 1);

  const mrr = calculateMRR(clients, offers);
  const arr = calculateARR(clients, offers);
  const cashThisMonth = calculateCashCollected(payments, monthStart, monthEnd);
  const cashYTD = calculateCashCollected(payments, yearStart, t);
  const outstandingAR = calculateOutstandingAR(payments);
  const projectYTD = calculateProjectRevenueYTD(payments, offers);

  const activeClients = clients.filter((c) => c.status === "active");
  const recurringClients = activeClients.filter((c) => c.primaryType === "recurring");
  const oneoffClients = activeClients.filter((c) => c.primaryType === "oneoff");
  const monthlyChurn = calculateMonthlyChurn(clients, t);
  const annualChurn = calculateAnnualChurn(monthlyChurn);
  const newThisMonth = calculateNewClientsThisMonth(clients);
  const churnedThisMonth = calculateChurnedThisMonth(clients);
  const netNew = newThisMonth - churnedThisMonth;

  const expected30 = calculateExpectedRevenue(payments, t, addDays(t, 30));
  const expected90 = calculateExpectedRevenue(payments, t, addDays(t, 90));
  const avgLTV = calculateAverageLTV(clients, payments);
  const avgLifetime = calculateAverageLifetime(clients);

  const row1 = [
    { label: "MRR", value: formatUSD(mrr), accent: "blue" },
    { label: "ARR", value: formatUSD(arr), accent: "blue" },
    { label: "Cash this month", value: formatUSD(cashThisMonth), accent: "green" },
    { label: "Cash YTD", value: formatUSD(cashYTD), accent: "green" },
    { label: "Outstanding AR", value: formatUSD(outstandingAR), accent: "amber" },
    { label: "Project revenue YTD", value: formatUSD(projectYTD), accent: "orange" },
  ];
  const row2 = [
    { label: "Active clients", value: activeClients.length },
    { label: "Recurring clients", value: recurringClients.length },
    { label: "One-off clients", value: oneoffClients.length },
    { label: "Monthly churn", value: formatPercent(monthlyChurn), accent: monthlyChurn > 5 ? "red" : "default" },
    { label: "Annual churn", value: formatPercent(annualChurn), accent: annualChurn > 30 ? "red" : "default" },
    { label: "Net new this month", value: netNew >= 0 ? `+${netNew}` : `${netNew}`, accent: netNew >= 0 ? "green" : "red" },
  ];
  const row3 = [
    { label: "Expected next 30 days", value: formatUSD(expected30), accent: "mint" },
    { label: "Expected next 90 days", value: formatUSD(expected90), accent: "mint" },
    { label: "Avg LTV (recurring)", value: formatUSD(avgLTV) },
    { label: "Avg lifetime (churned)", value: formatMonths(avgLifetime) },
  ];

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {row1.map((m) => <MetricCard key={m.label} {...m} />)}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {row2.map((m) => <MetricCard key={m.label} {...m} />)}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {row3.map((m) => <MetricCard key={m.label} {...m} />)}
      </div>
    </div>
  );
}
